import { Fragment, useCallback, useEffect, useState } from 'react'
import { EntityRef } from '../../components/EntityRef'
import { InlineAlert } from '../../components/InlineAlert'
import { StatusPill, type StatusPillVariant } from '../../components/StatusPill'
import { getSubmissionPackage, listAuthorityInteractions } from '../../api/authority'
import { listEvents } from '../../api/events'
import { ApiError } from '../../api/problem'
import { label } from '../../lib/labels'
import { useProgramRefs } from '../../lib/useProgramRefs'
import type { AuthorityInteractionView, SubmissionPackageView } from '../../types/authority'
import type { ProgramFilingDetailView } from '../../types/programFilingDetail'
import type { TimelineEntry } from '../../types/timeline'

export interface AuthorityPanelProps {
  filings: ProgramFilingDetailView[]
}

interface AuthorityRow {
  filing: ProgramFilingDetailView
  interaction: AuthorityInteractionView
}

const STATUS_VARIANT: Record<string, StatusPillVariant> = {
  draft: 'neutral',
  submitted: 'info',
  acknowledged: 'info',
  approved: 'success',
  denied: 'danger',
  deferred: 'warning',
  withdrawn: 'muted',
}

const TRANSITIONED_EVENT_TYPE = 'authority-interaction.transitioned.v1'

/**
 * Status-transition history for one `AuthorityInteraction` (mirrors `SpendPanel`'s
 * `AdjustmentHistoryRow` inline-expand pattern) — read over the universal Timeline `/events` feed
 * (`docs/adr-log.md` ADR-0046), filtered to this interaction's own
 * `authority-interaction.transitioned.v1` events (`AuthorityInteractionService.transition`'s own
 * emitted payload carries `{from, to}`). This is the interaction's real recorded status timeline,
 * not a fabricated one — an interaction still in `draft` has never transitioned, so it renders an
 * honest "no transitions yet" line rather than an empty table.
 */
function AuthorityStatusHistory({ interactionId }: { interactionId: string }) {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [entries, setEntries] = useState<TimelineEntry[]>([])
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    setState('loading')
    setError(null)
    listEvents({ targetType: 'AuthorityInteraction', targetId: interactionId, eventType: TRANSITIONED_EVENT_TYPE })
      .then((page) => {
        setEntries(page.items)
        setState('ready')
      })
      .catch((err) => {
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load status history.')
        setState('error')
      })
  }, [interactionId])

  useEffect(() => {
    load()
  }, [load])

  if (state === 'loading') {
    return (
      <p style={{ margin: 0, color: 'var(--iw-text-muted)' }} role="status">
        Loading status history…
      </p>
    )
  }

  if (state === 'error') {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={load}>
            Retry
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }

  if (entries.length === 0) {
    return <p style={{ margin: 0, color: 'var(--iw-text-muted)' }}>Still in draft — no status transitions recorded yet.</p>
  }

  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
      {entries.map((entry) => {
        const from = typeof entry.payload.from === 'string' ? entry.payload.from : '—'
        const to = typeof entry.payload.to === 'string' ? entry.payload.to : '—'
        return (
          <li key={entry.eventId} style={{ borderBottom: '1px solid var(--iw-border)', paddingBottom: 'var(--iw-space-2)' }}>
            <strong>
              {from} → {to}
            </strong>
            <p style={{ margin: '4px 0 0', color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
              {entry.actorId ? `Actor ${entry.actorId.slice(0, 8)}` : 'System'} ·{' '}
              {new Date(entry.occurredAt).toLocaleString()}
            </p>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * The linked `SubmissionPackage` (1:1, `AuthorityInteraction.submissionPkgRef`) for one interaction,
 * if any (`GET /submission-packages/{id}`, already published — `SubmissionPackageController.get`;
 * no additive backend route was needed, this story reads it directly by the id the interaction
 * already carries).
 */
function AuthoritySubmissionPackage({ submissionPkgRef }: { submissionPkgRef: string | null }) {
  const [state, setState] = useState<'loading' | 'ready' | 'error'>(submissionPkgRef ? 'loading' : 'ready')
  const [pkg, setPkg] = useState<SubmissionPackageView | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!submissionPkgRef) {
      setPkg(null)
      setState('ready')
      return
    }
    let cancelled = false
    setState('loading')
    setError(null)
    getSubmissionPackage(submissionPkgRef)
      .then((data) => {
        if (!cancelled) {
          setPkg(data)
          setState('ready')
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load the submission package.',
          )
          setState('error')
        }
      })
    return () => {
      cancelled = true
    }
  }, [submissionPkgRef])

  if (!submissionPkgRef) {
    return <p style={{ margin: 0, color: 'var(--iw-text-muted)' }}>No submission package created for this interaction yet.</p>
  }

  if (state === 'loading') {
    return (
      <p style={{ margin: 0, color: 'var(--iw-text-muted)' }} role="status">
        Loading submission package…
      </p>
    )
  }

  if (state === 'error') {
    return <InlineAlert severity="error">{error}</InlineAlert>
  }

  if (!pkg) {
    return <p style={{ margin: 0, color: 'var(--iw-text-muted)' }}>No submission package created for this interaction yet.</p>
  }

  return (
    <div>
      <p style={{ margin: 0 }}>
        <strong>{pkg.artifacts.length}</strong> artifact{pkg.artifacts.length === 1 ? '' : 's'} · generated{' '}
        {new Date(pkg.generatedAt).toLocaleString()}
      </p>
      {pkg.artifacts.length > 0 ? (
        <ul style={{ margin: '4px 0 0', paddingLeft: 'var(--iw-space-4)' }}>
          {pkg.artifacts.map((artifact, index) => (
            <li key={`${pkg.id}-${index}`} style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
              {artifact}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/**
 * The Case-workspace Authority tab (IB-14, GA demo lifecycle-tabs pass) — every `AuthorityInteraction`
 * across this case's `ProgramFiling`s (`GET .../authority-interactions`, per filing — already
 * published, `AuthorityInteractionController.list`), each row expandable to its real status-
 * transition history and its 1:1 `SubmissionPackage`. Both reads this tab depends on already
 * existed on the wire before this story — no additive backend route was needed. No write path
 * lives here — creating/transitioning an interaction, or generating its submission package, stays
 * wherever the Stage-2 application flow (IB-15/IB-16) owns that action; this is a read-only
 * lifecycle summary, mirroring `SpendPanel`/`CreditPanel`'s own posture on this same screen.
 */
export function AuthorityPanel({ filings }: AuthorityPanelProps) {
  const [rows, setRows] = useState<AuthorityRow[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const programRefs = useProgramRefs(filings.map((f) => f.programId))

  const load = useCallback(() => {
    setState('loading')
    setError(null)
    Promise.all(filings.map((filing) => listAuthorityInteractions(filing.id).then((page) => ({ filing, page }))))
      .then((results) => {
        const flattened: AuthorityRow[] = []
        let more = false
        for (const { filing, page } of results) {
          for (const interaction of page.items) {
            flattened.push({ filing, interaction })
          }
          if (page.nextCursor) more = true
        }
        flattened.sort((a, b) => (a.interaction.createdAt < b.interaction.createdAt ? 1 : -1))
        setRows(flattened)
        setHasMore(more)
        setState('ready')
      })
      .catch((err) => {
        setError(
          err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load authority interactions.',
        )
        setState('error')
      })
  }, [filings])

  useEffect(() => {
    load()
  }, [load])

  if (state === 'loading') {
    return <p style={{ color: 'var(--iw-text-muted)' }}>Loading authority interactions…</p>
  }

  if (state === 'error') {
    return (
      <InlineAlert
        severity="error"
        action={
          <button type="button" className="iw-btn iw-btn--sm" onClick={load}>
            Try again
          </button>
        }
      >
        {error}
      </InlineAlert>
    )
  }

  if (rows.length === 0) {
    return <p style={{ color: 'var(--iw-text-muted)' }}>No interactions recorded with any authority on this case yet.</p>
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
      <table className="iw-table" aria-label="Authority interactions">
        <thead>
          <tr>
            <th scope="col">Program</th>
            <th scope="col">Kind</th>
            <th scope="col">Status</th>
            <th scope="col">Created</th>
            <th scope="col">Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ filing, interaction }) => {
            const expanded = expandedId === interaction.id
            const kindLabel = label('authorityInteractionKind', interaction.kind)
            return (
              <Fragment key={interaction.id}>
                <tr>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    <EntityRef kind="program" id={filing.programId} name={programRefs.get(filing.programId)?.display} />
                  </td>
                  <td>{kindLabel}</td>
                  <td>
                    <StatusPill
                      variant={STATUS_VARIANT[interaction.status] ?? 'neutral'}
                      label={label('authorityInteractionStatus', interaction.status)}
                    />
                  </td>
                  <td style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-xs)' }}>
                    {new Date(interaction.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="iw-btn iw-btn--sm"
                      aria-expanded={expanded}
                      aria-label={`Details for ${kindLabel} interaction`}
                      onClick={() => setExpandedId(expanded ? null : interaction.id)}
                    >
                      {expanded ? 'Hide details' : 'Details'}
                    </button>
                  </td>
                </tr>
                {expanded ? (
                  <tr>
                    <td colSpan={5}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-3)' }}>
                        <div>
                          <h4 style={{ margin: '0 0 4px', fontSize: 'var(--iw-size-sm)' }}>Body</h4>
                          <p style={{ margin: 0, color: 'var(--iw-text-muted)' }}>{interaction.body}</p>
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--iw-space-4)', flexWrap: 'wrap', fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
                          <span>Created {new Date(interaction.createdAt).toLocaleString()}</span>
                          <span>Last updated {new Date(interaction.updatedAt).toLocaleString()}</span>
                          <span>Version {interaction.version}</span>
                        </div>
                        <div>
                          <h4 style={{ margin: '0 0 4px', fontSize: 'var(--iw-size-sm)' }}>Status history</h4>
                          <AuthorityStatusHistory interactionId={interaction.id} />
                        </div>
                        <div>
                          <h4 style={{ margin: '0 0 4px', fontSize: 'var(--iw-size-sm)' }}>Submission package</h4>
                          <AuthoritySubmissionPackage submissionPkgRef={interaction.submissionPkgRef} />
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            )
          })}
        </tbody>
      </table>
      {hasMore ? (
        <p style={{ margin: 0, color: 'var(--iw-text-subtle)', fontSize: 'var(--iw-size-xs)' }}>
          Some filings have more interactions than shown here — this is a summary view of the most
          recent page per filing, not the full history.
        </p>
      ) : null}
    </div>
  )
}
